import { gql, useMutation } from "@apollo/client";
import { useCallback, useState } from "react";

const EXTRACT_TEXT_FROM_URL = gql`
  mutation ExtractTextFromUrl($url: String!) {
    extractTextFromUrl(url: $url) {
      extractedText
      sourceUrl
    }
  }
`;

type ExtractResult = {
  extractedText: string;
  sourceUrl: string;
};

type UseExtractTextFromUrlReturn = {
  extract: (url: string) => void;
  loading: boolean;
  error: string | null;
  result: ExtractResult | null;
  reset: () => void;
};

export function useExtractTextFromUrl(): UseExtractTextFromUrlReturn {
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ExtractResult | null>(null);

  const [mutate, { loading }] = useMutation(EXTRACT_TEXT_FROM_URL, {
    onCompleted: (data) => {
      const r = data?.extractTextFromUrl;
      if (r) {
        setResult({
          extractedText: r.extractedText,
          sourceUrl: r.sourceUrl,
        });
        setError(null);
      }
    },
    onError: (e) => {
      setError(e.message || "Failed to extract content from URL");
      setResult(null);
    },
  });

  const extract = useCallback(
    (url: string) => {
      setError(null);
      setResult(null);
      mutate({ variables: { url } });
    },
    [mutate]
  );

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { extract, loading, error, result, reset };
}
